// Claude: Industry stats page — seller companies, buyer companies, both per top-level industry 13-03-2026
import React, { useEffect, useState } from "react";
import { Table } from "antd";
import { Button, Flex, Text } from "@chakra-ui/react";
import { MdChevronLeft } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { AdminAPI } from "../../Apis/adminAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";

const columns = [
  {
    title: "Industry",
    dataIndex: "name",
    key: "name",
    sorter: (a: any, b: any) => a.name.localeCompare(b.name),
    render: (val: string) => (
      <span style={val === "Industry Unassigned" ? { fontStyle: "italic", color: "#6B7280" } : {}}>
        {val}
      </span>
    ),
  },
  {
    title: "Seller Companies",
    dataIndex: "sellerCount",
    key: "sellerCount",
    sorter: (a: any, b: any) => a.sellerCount - b.sellerCount,
    defaultSortOrder: "descend" as const,
    align: "center" as const,
  },
  {
    // Claude: count of seller companies that target this industry via targetIndustryIds 15-04-2026
    title: "Seller Target Companies",
    dataIndex: "targetSellerCount",
    key: "targetSellerCount",
    sorter: (a: any, b: any) => a.targetSellerCount - b.targetSellerCount,
    align: "center" as const,
  },
  {
    title: "Buyer Companies",
    dataIndex: "buyerCount",
    key: "buyerCount",
    sorter: (a: any, b: any) => a.buyerCount - b.buyerCount,
    align: "center" as const,
  },
  {
    title: "Both",
    dataIndex: "bothCount",
    key: "bothCount",
    sorter: (a: any, b: any) => a.bothCount - b.bothCount,
    align: "center" as const,
  },
];

const IndustryStats = () => {
  const navigate = useNavigate();
  const { error } = useToastWrapper();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await AdminAPI.getIndustryStats();
        const rows = Array.isArray(res) ? res : (res?.data ?? []);
        setData(rows.map((r: any) => ({ ...r, key: r._id })));
      } catch (err: any) {
        error(errorHandler(err));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const totalSellers = data.reduce((sum, r) => sum + (r.sellerCount || 0), 0);
  const totalTargetSellers = data.reduce((sum, r) => sum + (r.targetSellerCount || 0), 0); // Claude: 15-04-2026
  const totalBuyers = data.reduce((sum, r) => sum + (r.buyerCount || 0), 0);

  return (
    <div className="p-4">
      <Flex align="center" justify="space-between" mb={4}>
        <Flex align="center" gap={2}>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<MdChevronLeft size={18} />}
            onClick={() => navigate("/admin/industry")}
            _hover={{ bg: "gray.100" }}
          >
            Back
          </Button>
          <Text fontSize="2xl" fontWeight={600}>Industry Stats</Text>
        </Flex>
      </Flex>

      {/* Claude: fine print note about double-counting 13-03-2026 */}
      <Text fontSize="11px" color="gray.500" mb={2} fontStyle="italic">
        Companies can be mapped to multiple industries. Hence total count may appear to be different from unique counts.
      </Text>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
        size="middle"
        bordered
        summary={() => (
          <Table.Summary.Row style={{ fontWeight: 600, background: "#F9FAFB" }}>
            <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="center">{totalSellers}</Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="center">{totalTargetSellers}</Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="center">{totalBuyers}</Table.Summary.Cell>
            <Table.Summary.Cell index={4} />
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default IndustryStats;
